import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { mapToActivityInfo } from '@/app/mappers/activityMappers'

const useVehicleService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const getVehicleList = async ({
    companyId,
    onlyActive = false,
  }: {
    companyId: string
    onlyActive?: boolean
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/vehicle/list${onlyActive ? '?isActive=true' : ''}`,
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }
  const getVehicleActivity = async ({
    companyId,
    vehicleId,
    startTime,
    endTime,
    slot,
  }: {
    companyId: string
    vehicleId: number
    startTime: string
    endTime: string
    slot: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/activity/vehicle?&vehicleId=${vehicleId}&startTime=${startTime}&endTime=${endTime}&slot=${slot}`,
      )
      const data = res.data
      if (!data) {
        return data
      }
   
      const mapped = mapToActivityInfo(data)
      return {
        ...data,
        ...mapped,
      }
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }
  const getVehicleGroups = async ({ companyId }: { companyId: string }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(`api/${companyId}/vehicle/groups`)
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getVehiclesInGroup = async ({
    companyId,
    groupId,
  }: {
    companyId: string
    groupId: number
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/vehicle/vehiclesInGroup?&groupId=${groupId}`,
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getVehicle = async ({
    companyId,
    vehicleId,
  }: {
    companyId: string
    vehicleId: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/vehicle/${vehicleId}`,
      )
      return res.data
    } catch (err) {
      return null
    } finally {
      stopLoading()
    }
  }

  const getVehicleLocks = async ({
    companyId,
    vehicleId,
  }: {
    companyId: string
    vehicleId: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/vehicle/vehicleLocks?&id=${vehicleId}`,
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const saveVehicle = async ({
    companyId,
    vehicle,
  }: {
    companyId: string
    vehicle: any
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(`api/${companyId}/vehicle/save`, {
        vehicle: {
          ...vehicle,
          vehicleType: vehicle.vehicleType
            ? parseInt(vehicle.vehicleType! as any)
            : undefined,
        },
      })
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const activateVehicle = async ({
    companyId,
    vehicles,
  }: {
    companyId: string
    vehicles: Array<string>
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/vehicle/activate`,
        {
          vehicles,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const deleteVehicle = async ({
    companyId,
    vehicles,
  }: {
    companyId: string
    vehicles: Array<string>
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(`api/${companyId}/vehicle/delete`, {
        vehicles,
      })
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const addVehicleGroup = async ({
    companyId,
    name,
  }: {
    companyId: string
    name: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/vehicle/addGroup`,
        {
          name,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const addVehicleToGroup = async ({
    companyId,
    groupId,
    vehicles,
  }: {
    companyId: string
    groupId: string
    vehicles: Array<string>
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/vehicle/addVehicleToGroup`,
        {
          groupId,
          vehicles,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const removeVehicleFromGroup = async ({
    companyId,
    groupId,
    vehicles,
  }: {
    companyId: string
    groupId: string
    vehicles: Array<string>
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/vehicle/removeVehicleFromGroup`,
        {
          groupId,
          vehicles,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const deleteVehicleGroups = async ({
    companyId,
    groups,
  }: {
    companyId: string
    groups: Array<number>
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/vehicle/removeVehicleGroups`,
        {
          groups,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  return {
    getVehicleList,
    getVehicleGroups,
    getVehiclesInGroup,
    getVehicle,
    saveVehicle,
    activateVehicle,
    deleteVehicle,
    addVehicleGroup,
    addVehicleToGroup,
    removeVehicleFromGroup,
    getVehicleActivity,
    deleteVehicleGroups,
    getVehicleLocks,
  }
}

export default useVehicleService
